import { useEffect, useState } from 'react';
import { Rows, getRowsParamsType, useRowSourceParamsType, useRowSourceReturnType } from '../models';
import { logger } from '../../../../utils';

const DEFAULT_CACHE_BLOCK_SIZE = 40;

export const useRowSource = ({
  data,
  fetchNextPage,
  isFetching,
  isFetchingNextPage,
  hasNextPage,
}: useRowSourceParamsType): useRowSourceReturnType => {
  let rows: Rows;
  const [serverSideInitialRowCount, setServerSideInitialRowCount] = useState<number>();

  // for now only allow 1 maxConcurrentDatasourceRequests, need to change to
  // queue to support multiple concurrent requests
  const [getRowsCallback, setGetRowsCallback] = useState<getRowsParamsType>();

  /**
   * Retrieves rows for the grid based on the provided parameters.
   * only applies to server-side datasource model
   *
   * @param params.success - The success callback function to be called when rows are successfully retrieved.
   * @param params.fail - The fail callback function to be called when rows cannot be retrieved.
   * @param params.request - The request object containing information about the rows to retrieve.
   */
  const getRows = (params: getRowsParamsType) => {
    const { success, fail, request } = params;
    const { startRow, endRow } = request;
    let needCallback = false;

    logger.log('Grid useRowSource getRows:', {
      startRow,
      endRow,
      dataLength: data?.length,
      hasNextPage,
      request,
    });

    if (startRow === undefined || endRow === undefined) {
      logger.log('Grid useRowSource getRows: invalid request parameters');
      fail();
    } else if (isFetching || isFetchingNextPage) {
      logger.log('Grid useRowSource getRows: fetch in progress, setGetRowsCallback');
      needCallback = true;
    } else if (hasNextPage === false || data?.length >= endRow) {
      logger.log('Grid useRowSource getRows: success');
      success({ rowData: data?.slice?.(startRow, endRow) || [], rowCount: hasNextPage ? undefined : data?.length });
    } else if (fetchNextPage !== undefined) {
      logger.log('Grid useRowSource getRows: fetchNextPage()');
      needCallback = true;
      fetchNextPage();
    } else {
      logger.log('Grid useRowSource getRows: invalid case', {
        hasNextPage,
        isFetching,
        isFetchingNextPage,
        data,
      });
      fail();
    }

    if (needCallback) {
      setGetRowsCallback(Object.assign({}, params));
      logger.log('Grid useRowSource getRows need call back:', { getRowsCallback });
    }
  };

  // detect serverSideInitialRowCount from the first data fetch
  useEffect(() => {
    if (serverSideInitialRowCount === undefined && data?.length > 0) {
      logger.log('Grid useRowSource setServerSideInitialRowCount:', {
        serverSideInitialRowCount,
        newserverSideInitialRowCount: data.length,
      });
      setServerSideInitialRowCount(data.length);
    }
  }, [data?.length > 0, !!serverSideInitialRowCount, setServerSideInitialRowCount]);

  useEffect(() => {
    logger.log('Grid useRowSource useEffect:');
    if (getRowsCallback) {
      const getRowsCallbackCopy = Object.assign({}, getRowsCallback);
      setGetRowsCallback(undefined);
      getRows(getRowsCallbackCopy);
    } else {
      logger.log('Grid useRowSource useEffect: no getRowsCallback set');
    }
  }, [data?.length]);

  // implicit scroll/pagination datasource model if fetchNextPage is provided
  if (fetchNextPage === undefined) {
    rows = data || [];
  } else {
    rows = {
      getRows,
      cacheBlockSize:
        serverSideInitialRowCount !== undefined && serverSideInitialRowCount > DEFAULT_CACHE_BLOCK_SIZE
          ? serverSideInitialRowCount
          : DEFAULT_CACHE_BLOCK_SIZE,
    };
  }

  // logger.log('Grid useRowSource:', { data, fetchNextPage, rows, serverSideInitialRowCount });

  return { rows, serverSideInitialRowCount };
};
